import React from 'react'
import styled from 'styled-components'
import {Skeleton} from '../../BaseComponents'
import type {DonutTone} from '../DonutCard'

/**
 * BarTrack (internal)
 *
 * One horizontal bar: a `--viz-track` pill with a fill whose width is the figure's share of the
 * track. BarList draws one per row and ComparisonBars two, so the bars look the same everywhere.
 *
 * - `percent` null draws no fill at all (no figure); 0 draws an empty fill; anything above 0 is
 *   at least `--space-1` wide, so a small figure still shows.
 * - `loading` puts a skeleton in the track instead of the fill.
 * - `size="md"` (default) is `--space-3` tall, BarList's height; `size="sm"` is `--space-2`, the
 *   mock's compact bars (reliability parts, Against the team).
 *
 * The fill's width is an inline style: it is a computed value, and one class per width would
 * grow the stylesheet with every distinct figure.
 */

/** BarList tones: the `--viz-*` scale plus semantic status tones, for urgency ramps. */
export type BarTone = DonutTone | 'success' | 'error'

/**
 * `neutral` is `--neutral-600`, not DonutCard's `--neutral-500`: the bar is the whole mark, and
 * `--neutral-500` is under 3:1 against the card (WCAG 1.4.11).
 */
export const BAR_TONE_VARS: Record<BarTone, string> = {
  cyan: 'var(--viz-cyan)',
  'cyan-soft': 'var(--viz-cyan-soft)',
  blue: 'var(--viz-blue)',
  'blue-soft': 'var(--viz-blue-soft)',
  navy: 'var(--viz-navy)',
  'navy-soft': 'var(--viz-navy-soft)',
  warning: 'var(--warning-500)',
  success: 'var(--success-500)',
  error: 'var(--error-500)',
  neutral: 'var(--neutral-600)',
}

export type BarTrackSize = 'sm' | 'md'

const HEIGHT: Record<BarTrackSize, string> = {
  sm: 'var(--space-2)',
  md: 'var(--space-3)',
}

export interface BarTrackProps {
  /** The fill's share of the track, 0–100 (clamped). null draws no fill. */
  percent: number | null
  /** The fill's colour (default cyan). */
  tone?: BarTone
  size?: BarTrackSize
  /** A skeleton in place of the fill. */
  loading?: boolean
  /** On the track; the fill gets `${testId}-fill`. */
  'data-testid'?: string
}

const Track = styled.div<{$size: BarTrackSize}>`
  height: ${({$size}) => HEIGHT[$size]};
  background-color: var(--viz-track);
  border-radius: var(--radius-pill);
  overflow: hidden;
`

const Fill = styled.div<{$tone: BarTone; $minimum: boolean}>`
  height: 100%;
  min-width: ${({$minimum}) => ($minimum ? 'var(--space-1)' : '0')};
  background-color: ${({$tone}) => BAR_TONE_VARS[$tone]};
  border-radius: var(--radius-pill);
  transition: width 0.4s ease;
`

/** Clamp a share to 0–100; NaN and negatives are 0. */
const clampPercent = (percent: number): number =>
  Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : 0

export function BarTrack({
  percent,
  tone = 'cyan',
  size = 'md',
  loading = false,
  'data-testid': testId,
}: BarTrackProps) {
  let fill: React.ReactNode = null
  if (loading) {
    fill = <Skeleton height="100%" />
  } else if (percent !== null) {
    const width = clampPercent(percent)
    fill = (
      <Fill
        $tone={tone}
        $minimum={width > 0}
        style={{width: `${width}%`}}
        data-testid={testId ? `${testId}-fill` : undefined}
      />
    )
  }

  return (
    <Track $size={size} data-testid={testId}>
      {fill}
    </Track>
  )
}
